import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { ArrowLeft, Plus, Trash2, Save, Printer, Send, CheckCircle2, XCircle, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtINR, openPdf } from "@/lib/documents";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { useAuth } from "@/contexts/AuthContext";
import { useHotelContext } from "@/contexts/HotelContext";
import { quotationPdfUrl, STATUS_TONE, fmtD } from "./Quotations";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const newLine = (itemTypes, type = "room") => ({ id: Math.random().toString(36).slice(2, 10), description: "", item_type: type, quantity: 1, unit_price: "", tax_rate: itemTypes.find((t) => t.code === type)?.tax_rate ?? 0 });
const F = ({ label, children, className = "" }) => <div className={className}><Label className="text-[11px] mono uppercase text-hocp-textMuted">{label}</Label>{children}</div>;
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

export default function QuotationEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { ctx } = useHotelContext();
  const hotelId = ctx?.hotel_id || "";
  const [meta, setMeta] = useState({ types: [], item_types: [], default_terms: [] });
  const [q, setQ] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/quotations/meta").then((r) => {
      setMeta(r.data);
      if (isNew) setForm({ customer: { name: "", phone: "", email: "", company: "" }, quotation_date: today(), valid_until: plusDays(14), quotation_types: ["room_booking"],
        event_details: "", items: [newLine(r.data.item_types)], notes: "", terms: r.data.default_terms.join("\n") });
    }).catch((e) => toast.error(formatApiError(e)));
  }, [isNew]);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/quotations/${id}`);
      setQ(data);
      setForm({ customer: { name: "", phone: "", email: "", company: "", ...data.customer }, quotation_date: data.quotation_date, valid_until: data.valid_until, quotation_types: data.quotation_types?.length ? data.quotation_types : [data.quotation_type].filter(Boolean),
        event_details: data.event_details || "", items: data.items.map((l) => ({ ...l })), notes: data.notes || "", terms: data.terms || "" });
    } catch (e) { toast.error(formatApiError(e)); navigate("/quotations"); }
  }, [id, isNew, navigate]);
  useEffect(() => { load(); }, [load]);

  const locked = !!q && ["accepted", "rejected"].includes(q.status);
  const canEdit = isNew ? hasPermission("quotations.create") : hasPermission("quotations.edit") && !locked;

  const totals = useMemo(() => {
    let sub = 0, tax = 0; const by = {};
    (form?.items || []).forEach((l) => { const s = num(l.quantity) * num(l.unit_price); const t = s * num(l.tax_rate) / 100; sub += s; tax += t; if (t) by[l.tax_rate] = (by[l.tax_rate] || 0) + t; });
    return { subtotal: sub, tax, grand: sub + tax, by };
  }, [form?.items]);

  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  const setType = (i, type) => setItem(i, { item_type: type, tax_rate: meta.item_types.find((t) => t.code === type)?.tax_rate ?? 0 });

  const save = async () => {
    if (!form.customer.name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.quotation_types.length) { toast.error("Select at least one quotation type"); return; }
    if (!form.items.length || form.items.some((l) => !l.description.trim())) { toast.error("Every line item needs a description"); return; }
    if (form.items.some((l) => !(num(l.quantity) > 0))) { toast.error("Quantity must be greater than zero"); return; }
    setSaving(true);
    try {
      const body = { ...form, event_details: form.event_details || null, notes: form.notes || null, terms: form.terms || null,
        items: form.items.map((l) => ({ id: l.id, description: l.description.trim(), item_type: l.item_type, quantity: num(l.quantity), unit_price: num(l.unit_price), tax_rate: l.tax_rate === "" ? null : num(l.tax_rate) })) };
      if (isNew) {
        const { data } = await api.post(`/quotations/`, body, { params: hotelId ? { hotel_id: hotelId } : {} });
        toast.success(`Quotation ${data.number} created`);
        navigate(`/quotations/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/quotations/${id}`, body);
        setQ(data); toast.success("Quotation saved");
      }
    } catch (e) {
      const b = e?.response?.data || {}; const d = b.code ? b : b.detail || {};
      toast.error(d.message || formatApiError(e));
    }
    setSaving(false);
  };

  const setStatus = async (status) => {
    try { const { data } = await api.post(`/quotations/${id}/status`, { status }); setQ(data); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete draft ${q.number}?`)) return;
    try { await api.delete(`/quotations/${id}`); toast.success("Quotation deleted"); navigate("/quotations"); }
    catch (e) { const b = e?.response?.data || {}; toast.error((b.code ? b : b.detail || {}).message || formatApiError(e)); }
  };

  if (!form) return <div className="p-8 text-hocp-textMuted">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="quotation-editor">
      <PageHeader title={isNew ? "New Quotation" : `Quotation ${q?.number || ""}`} subtitle={isNew ? "Customised proposal for a prospective customer" : `${q?.quotation_type_label || ""} · ${q?.customer?.name || ""}`}
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/quotations")} data-testid="back-to-quotations"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          {!isNew && <>
            <Button variant="outline" onClick={() => openPdf(quotationPdfUrl(id), `Quotation-${q?.number}.pdf`)} data-testid="print-quotation"><Printer className="w-4 h-4 mr-2" />Print</Button>
            <DownloadPdfButton url={quotationPdfUrl(id)} filename={`Quotation-${q?.number}.pdf`} label="Download PDF" size="lg" className="h-10" testId="download-quotation" />
          </>}
          {canEdit && <Button onClick={save} disabled={saving} data-testid="save-quotation"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : isNew ? "Create Quotation" : "Save changes"}</Button>}
        </div>} />

      {q && (
        <Card className="p-3 flex flex-wrap items-center gap-2" data-testid="quotation-status-bar">
          <span className="text-[11px] mono uppercase text-hocp-textMuted">Status</span>
          <Badge tone={STATUS_TONE[q.status]} data-testid="quotation-status">{q.status}</Badge>
          <span className="text-[12px] text-hocp-textMuted">· valid until {fmtD(q.valid_until)}{locked ? " · locked (accepted/rejected quotations cannot be edited)" : ""}</span>
          {hasPermission("quotations.edit") && (
            <div className="ml-auto flex flex-wrap gap-1.5">
              {q.status !== "sent" && !locked && <Button size="sm" variant="outline" onClick={() => setStatus("sent")} data-testid="mark-sent"><Send className="w-3.5 h-3.5 mr-1" />Mark sent</Button>}
              {q.status !== "accepted" && <Button size="sm" variant="outline" onClick={() => setStatus("accepted")} data-testid="mark-accepted"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Accepted</Button>}
              {q.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => setStatus("rejected")} data-testid="mark-rejected"><XCircle className="w-3.5 h-3.5 mr-1" />Rejected</Button>}
              {q.status !== "expired" && !locked && <Button size="sm" variant="outline" onClick={() => setStatus("expired")} data-testid="mark-expired"><Clock className="w-3.5 h-3.5 mr-1" />Expired</Button>}
              {locked && <Button size="sm" variant="outline" onClick={() => setStatus("draft")} data-testid="reopen-draft">Reopen as draft</Button>}
              {q.status === "draft" && hasPermission("quotations.delete") && <Button size="sm" variant="outline" className="text-red-600" onClick={remove} data-testid="delete-quotation"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
            </div>
          )}
        </Card>
      )}

      <fieldset disabled={!canEdit} className="space-y-4 disabled:opacity-90">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4 space-y-3">
            <h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500">Customer</h3>
            <F label="Customer name *"><Input value={form.customer.name} onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })} data-testid="q-customer-name" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Contact number"><Input value={form.customer.phone || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} data-testid="q-customer-phone" /></F>
              <F label="Email"><Input type="email" value={form.customer.email || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, email: e.target.value } })} data-testid="q-customer-email" /></F>
            </div>
            <F label="Company / organisation"><Input value={form.customer.company || ""} onChange={(e) => setForm({ ...form, customer: { ...form.customer, company: e.target.value } })} data-testid="q-customer-company" /></F>
          </Card>
          <Card className="p-4 space-y-3">
            <h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500">Quotation</h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Quotation date"><DatePicker value={form.quotation_date} onChange={(v) => setForm({ ...form, quotation_date: v })} data-testid="q-date" /></F>
              <F label="Valid until"><DatePicker value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} minDate={form.quotation_date} data-testid="q-valid-until" /></F>
            </div>
            <F label="Quotation type · select one or more">
              <div className="flex flex-wrap gap-1.5 pt-1" role="group" aria-label="Quotation types" data-testid="q-types">
                {meta.types.map((t) => {
                  const on = form.quotation_types.includes(t.code);
                  return (
                    <button key={t.code} type="button" aria-pressed={on} disabled={!canEdit} data-testid={`q-type-${t.code}`} data-selected={on ? "1" : "0"}
                      onClick={() => setForm({ ...form, quotation_types: on ? form.quotation_types.filter((c) => c !== t.code) : [...form.quotation_types, t.code] })}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${on ? "bg-[#DBFCEF] text-[#014751] border-[#014751] ring-1 ring-[#014751]" : "bg-white text-hocp-textSecondary border-hocp-border hover:border-hocp-deepTeal"}`}>
                      {on ? "✓ " : ""}{t.label}
                    </button>
                  );
                })}
              </div>
              {!form.quotation_types.length && <div className="text-[11.5px] text-red-600 mt-1" data-testid="q-types-error">Select at least one type</div>}
            </F>
            <F label="Event / stay summary (shown under the header)"><Input value={form.event_details} onChange={(e) => setForm({ ...form, event_details: e.target.value })} placeholder="e.g. 12–14 Nov 2026 · 10 Deluxe rooms · 20 guests" data-testid="q-event-details" /></F>
            <div className="text-[11.5px] text-hocp-textMuted">Hotel letterhead (logo, address, contact) is added automatically from the Hotel Profile.</div>
          </Card>
        </div>

        <Card className="p-4 space-y-3" data-testid="q-items-card">
          <div className="flex items-center justify-between">
            <h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500">Line items · taxes apply by item type</h3>
            {canEdit && <Button size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, newLine(meta.item_types, form.quotation_types.includes("banquet_event") ? "banquet" : "room")] })} data-testid="add-line-item"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="q-items-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-hocp-textMuted border-b border-hocp-border">
                <th className="py-2 pr-2 w-8">#</th><th className="py-2 pr-2">Description</th><th className="py-2 pr-2 w-44">Item type</th><th className="py-2 pr-2 w-20">Qty</th><th className="py-2 pr-2 w-32">Unit price</th><th className="py-2 pr-2 w-20">Tax %</th><th className="py-2 pr-2 w-28 text-right">Total</th><th className="w-8"></th>
              </tr></thead>
              <tbody>
                {form.items.map((l, i) => {
                  const s = num(l.quantity) * num(l.unit_price), t = s * num(l.tax_rate) / 100;
                  return (
                    <tr key={l.id} className="border-b border-hocp-borderSubtle last:border-0 align-top" data-testid={`q-line-${i + 1}`}>
                      <td className="py-2 pr-2 mono text-hocp-textMuted">{i + 1}</td>
                      <td className="py-2 pr-2"><Input value={l.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="e.g. Deluxe Room · 2 nights" data-testid={`q-line-desc-${i + 1}`} /></td>
                      <td className="py-2 pr-2">
                        <Select value={l.item_type} onValueChange={(v) => setType(i, v)}>
                          <SelectTrigger data-testid={`q-line-type-${i + 1}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{meta.item_types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label} · {t.tax_rate}%</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 pr-2"><Input type="number" min="0" step="1" value={l.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} className="mono" data-testid={`q-line-qty-${i + 1}`} /></td>
                      <td className="py-2 pr-2"><Input type="number" min="0" step="1" value={l.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} className="mono" placeholder="0" data-testid={`q-line-price-${i + 1}`} /></td>
                      <td className="py-2 pr-2"><Input type="number" min="0" max="100" step="0.5" value={l.tax_rate} onChange={(e) => setItem(i, { tax_rate: e.target.value })} className="mono" data-testid={`q-line-tax-${i + 1}`} /></td>
                      <td className="py-2 pr-2 text-right mono font-semibold pt-4" data-testid={`q-line-total-${i + 1}`}>{fmtINR(s + t)}<div className="text-[10.5px] text-hocp-textMuted font-normal">{fmtINR(s)} + {fmtINR(t)} tax</div></td>
                      <td className="py-2 pt-3">{canEdit && <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })} className="text-hocp-textMuted hover:text-red-600" aria-label="Remove line" data-testid={`q-line-remove-${i + 1}`}><Trash2 className="w-4 h-4" /></button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <table className="text-[13px] w-72" data-testid="q-totals">
              <tbody>
                <tr><td className="py-1 text-hocp-textMuted">Subtotal</td><td className="py-1 text-right mono" data-testid="q-subtotal">{fmtINR(totals.subtotal)}</td></tr>
                {Object.entries(totals.by).map(([r, a]) => <tr key={r}><td className="py-1 text-hocp-textMuted">Tax @ {r}%</td><td className="py-1 text-right mono">{fmtINR(a)}</td></tr>)}
                <tr className="border-t-2 border-[#021F21]"><td className="py-2 font-bold text-hocp-deepTeal">Grand total</td><td className="py-2 text-right mono font-bold text-hocp-deepTeal text-[15px]" data-testid="q-grand-total">{fmtINR(totals.grand)}</td></tr>
              </tbody>
            </table>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4 space-y-2"><h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500">Special notes</h3>
            <Textarea rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Inclusions, complimentary items, payment schedule…" data-testid="q-notes" /></Card>
          <Card className="p-4 space-y-2"><h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500">Terms &amp; conditions <span className="normal-case tracking-normal text-hocp-textMuted">· one per line</span></h3>
            <Textarea rows={5} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="q-terms" /></Card>
        </div>
      </fieldset>
    </div>
  );
}
